import { Component, type ErrorInfo, type ReactNode, useEffect } from "react";
import { settle } from "./opening.tsx";
import { ReportThis } from "./ReportThis.tsx";
import { parse } from "./router.ts";
import { Button } from "./souvenir.ts";
import { BackToTrips, Message } from "./ui.tsx";

// A crash says so (features/opening.md): a page that throws while it's drawn shows the failure pages' look,
// never a blank page. Caught at the app's root, as a page (main.tsx), and around a trip's view, where the
// trip's bar and tabs stay (TripPage.tsx): another tab draws afresh, so one view that broke never takes the
// others with it. What broke goes to the console, never on the page; Report this opens the feedback form
// (features/feedback.md), which sends the page's address and the crash with it (its error and where in
// Tripmo's code, src/feedback/crash.ts) while its switch is on.

const TITLE = "Something went wrong on our side";

// What broke, in the words under the title: "this page", or a trip's view by its name ("Ideas", "the map").
const body = (what: string) => `Tripmo ran into a problem showing ${what}. Reloading may put it right.`;

interface CaughtProps {
  what: string;
  // What's shown instead, given what it broke on.
  fallback: (crash: { error: unknown }) => ReactNode;
  // What's shown changing (another tab) draws it afresh.
  reset?: string;
  children: ReactNode;
}

interface CaughtState {
  // What it broke on, while it's broken.
  broke: { error: unknown } | null;
  // The `reset` it was drawn for.
  drawn: string | undefined;
}

class Caught extends Component<CaughtProps, CaughtState> {
  override state: CaughtState = { broke: null, drawn: this.props.reset };

  // Another view in its place starts afresh, before it's drawn (so it isn't drawn broken first).
  static getDerivedStateFromProps(props: CaughtProps, state: CaughtState): Partial<CaughtState> | null {
    return props.reset === state.drawn ? null : { broke: null, drawn: props.reset };
  }

  static getDerivedStateFromError(error: unknown): Partial<CaughtState> {
    return { broke: { error } };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error(`Tripmo couldn't draw ${this.props.what}:`, error, info.componentStack ?? "");
  }

  // Going somewhere else in the app (the logo, Back to your trips, a tab, Back) draws it again.
  private again = () => {
    if (this.state.broke) this.setState({ broke: null });
  };

  override componentDidMount() {
    addEventListener("popstate", this.again);
    addEventListener("tripmo:navigate", this.again);
  }

  override componentWillUnmount() {
    removeEventListener("popstate", this.again);
    removeEventListener("tripmo:navigate", this.again);
  }

  override render() {
    return this.state.broke ? this.props.fallback(this.state.broke) : this.props.children;
  }
}

// The whole app: the failure page, with Reload (and on a trip, Back to your trips).
export function CaughtApp({ children }: { children: ReactNode }) {
  return (
    <Caught what="this page" fallback={(crash) => <PageBroke crash={crash} />}>
      {children}
    </Caught>
  );
}

// A trip's view (the map, Days, Ideas, ..., Trip settings), named as its tab is (`what`), and which one it
// is (`view`): another drawn in its place starts afresh. With `className`, the view's own box (a `div`),
// which what says it broke takes the place of.
export function CaughtView({
  what,
  view,
  className,
  children,
}: {
  what: string;
  view: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Caught what={what} reset={view} fallback={(crash) => <ViewBroke what={what} crash={crash} />}>
      {className ? <div className={className}>{children}</div> : children}
    </Caught>
  );
}

function PageBroke({ crash }: { crash: { error: unknown } }) {
  // The sunrise, if it's still up, fades into this page as into any other (opening.tsx): the page that
  // held it is gone, and if nothing had held it yet (it broke as it was first drawn), nothing does now.
  useEffect(() => queueMicrotask(settle), []);
  return (
    <Message
      title={TITLE}
      body={body("this page")}
      action={
        <div className="tm-message-actions">
          <Reload />
          {parse().name === "trip" && <BackToTrips />}
          <ReportThis line always crash={crash} />
        </div>
      }
    />
  );
}

// Where the view goes, under the trip's bar and above its tabs; the bar's logo is Tripmo's mark here.
function ViewBroke({ what, crash }: { what: string; crash: { error: unknown } }) {
  return (
    <section className="tm-message tm-view-broke" aria-labelledby="view-broke">
      <h2 id="view-broke" className="display-l">
        {TITLE}
      </h2>
      <p className="body">{body(what)}</p>
      <div className="tm-message-actions">
        <Reload />
        <BackToTrips />
        <ReportThis line always crash={crash} />
      </div>
    </section>
  );
}

function Reload() {
  return (
    <Button variant="primary" size="sm" onClick={() => location.reload()}>
      Reload
    </Button>
  );
}
